import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShoppingCart, FiPlus, FiMinus, FiTrash2, FiCheck } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Container = styled.div`
  min-height: calc(100vh - 200px);
  padding: 60px 20px;
  background: var(--background-dark);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 32px;
  font-weight: 700;
`;

const CartButton = styled.button`
  padding: 0;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border: none;
  border-radius: 50%;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: fixed;
  bottom: 100px;
  right: 30px;
  z-index: 999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 24px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4);
  }

  @media (max-width: 768px) {
    bottom: 100px;
    right: 20px;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #e74c3c;
  color: #fff;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const MembershipInfo = styled.div`
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: #fff;
  padding: 20px;
  border-radius: 15px;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`;

const MembershipText = styled.div`
  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
  }
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 14px;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  overflow-x: auto;
  padding-bottom: 10px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--background-lighter);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 10px;
  }
`;

const CategoryTab = styled.button`
  padding: 12px 24px;
  background: ${props => props.$active ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : 'var(--background-lighter)'};
  color: ${props => props.$active ? '#fff' : 'var(--text-primary)'};
  border: 2px solid ${props => props.$active ? 'transparent' : 'var(--border-color)'};
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--primary-color);
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
`;

const ItemCard = styled(motion.div)`
  background: var(--background-lighter);
  border: 2px solid var(--border-color);
  border-radius: 15px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.2);
    transform: translateY(-4px);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
`;

const ItemName = styled.h3`
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const ItemCategory = styled.span`
  font-size: 11px;
  color: var(--text-secondary);
  background: var(--background-dark);
  padding: 4px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ItemPrice = styled.div`
  margin: 12px 0;
`;

const Price = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-color);
  
  ${props => props.$discounted && `
    display: flex;
    align-items: center;
    gap: 10px;
  `}
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
  text-decoration: line-through;
`;

const DiscountBadge = styled.span`
  background: #e74c3c;
  color: #fff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const MembershipPrice = styled.div`
  font-size: 14px;
  color: #4caf50;
  font-weight: 600;
  margin-top: 4px;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid var(--primary-color);
  background: var(--primary-color);
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;

  &:hover {
    background: var(--secondary-color);
    border-color: var(--secondary-color);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Quantity = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 30px;
  text-align: center;
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const CartSidebar = styled(motion.div)`
  position: fixed;
  right: 0;
  top: 0;
  width: 400px;
  height: 100vh;
  background: var(--background-lighter);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  padding: 30px;
  overflow-y: auto;
  z-index: 1100;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CartOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1099;
`;

const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--border-color);
`;

const CartTitle = styled.h2`
  color: var(--text-primary);
  font-size: 24px;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const CartItems = styled.div`
  margin-bottom: 20px;
`;

const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: var(--background-dark);
  border-radius: 10px;
  margin-bottom: 10px;
`;

const CartItemInfo = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 5px 0;
    color: var(--text-primary);
    font-size: 14px;
  }
  
  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 12px;
  }
`;

const CartItemPrice = styled.div`
  font-weight: 600;
  color: var(--primary-color);
  margin: 0 15px;
`;

const RemoveButton = styled.button`
  background: #e74c3c;
  border: none;
  color: #FFFFFF;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;

  &:hover {
    background: #c0392b;
  }
`;

const CartSummary = styled.div`
  border-top: 2px solid var(--border-color);
  padding-top: 20px;
  margin-top: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  color: var(--text-primary);
  
  ${props => props.$total && `
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color);
    margin-top: 15px;
    padding-top: 15px;
    border-top: 2px solid var(--border-color);
  `}
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #4caf50, #45a049);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
`;

const SelectItems = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Items' },
    { value: 'steam_iron', label: 'Steam Iron' },
    { value: 'dry_clean', label: 'Dry Clean' },
    { value: 'laundry', label: 'Laundry' },
    { value: 'shoes_cleaning', label: 'Shoes Cleaning' }
  ];

  useEffect(() => {
    fetchData();
    
    // Load cart from sessionStorage on mount
    const savedCart = sessionStorage.getItem('orderCart');
    const savedMembership = sessionStorage.getItem('orderMembership');
    
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (err) {
        console.error('Error loading cart:', err);
      }
    }
    
    if (savedMembership) {
      try {
        setMembership(JSON.parse(savedMembership));
      } catch (err) {
        console.error('Error loading membership:', err);
      }
    }

    // Listen for global cart button click
    const handleOpenCart = () => {
      setShowCart(true);
    };

    window.addEventListener('openCart', handleOpenCart);

    return () => {
      window.removeEventListener('openCart', handleOpenCart);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch items
      const itemsResponse = await api.getItems();
      if (itemsResponse.success) {
        setItems(itemsResponse.data || []);
      }

      // Fetch membership if user is logged in
      if (user) {
        try {
          const membershipResponse = await api.getMembership();
          if (membershipResponse.success && membershipResponse.data) {
            setMembership(membershipResponse.data);
            sessionStorage.setItem('orderMembership', JSON.stringify(membershipResponse.data));
          }
        } catch (err) {
          // User doesn't have membership, that's okay
          console.log('No active membership found');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique subcategories for the selected category
  const availableSubcategories = selectedCategory === 'all'
    ? []
    : [...new Set(items
        .filter(item => item.service_type === selectedCategory && item.subcategory)
        .map(item => item.subcategory))]
        .sort();

  const filteredItems = items.filter(item => {
    // Filter by service type
    if (selectedCategory !== 'all' && item.service_type !== selectedCategory) {
      return false;
    }
    // Filter by subcategory
    if (selectedSubcategory !== 'all' && item.subcategory !== selectedSubcategory) {
      return false;
    }
    return true;
  });

  const addToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    
    let newCart;
    if (existingItem) {
      newCart = cart.map(cartItem => 
        cartItem.id === item.id 
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      );
    } else {
      newCart = [...cart, { ...item, quantity: 1 }];
    }
    
    setCart(newCart);
    sessionStorage.setItem('orderCart', JSON.stringify(newCart));
  };

  const updateQuantity = (itemId, change) => {
    const item = cart.find(i => i.id === itemId);
    if (!item) return;
    
    const newQuantity = item.quantity + change;
    
    // If decrementing to 0, remove the item completely
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    // Otherwise update quantity
    const newCart = cart.map(i => 
      i.id === itemId ? { ...i, quantity: newQuantity } : i
    );
    
    setCart(newCart);
    sessionStorage.setItem('orderCart', JSON.stringify(newCart));
  };

  const removeFromCart = (itemId) => {
    const newCart = cart.filter(item => item.id !== itemId);
    setCart(newCart);
    sessionStorage.setItem('orderCart', JSON.stringify(newCart));
  };

  const calculateTotal = () => {
    if (!cart.length) return { subtotal: 0, discount: 0, total: 0, itemCount: 0, membershipDiscount: 0 };

    let itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    
    // Calculate regular subtotal first
    const regularSubtotal = cart.reduce((sum, item) => {
      const price = item.price;
      return sum + (item.quantity * price);
    }, 0);
    
    // Calculate membership discount for Daily Wear items only
    let membershipDiscount = 0;
    let membershipItemsCount = 0;
    
    if (membership && membership.clothes_remaining > 0) {
      let remainingCoverage = membership.clothes_remaining;
      
      cart.forEach(item => {
        // Only Steam Iron Daily Wear items are eligible
        const isServiceTypeSteamIron = item.service_type && item.service_type.toLowerCase() === 'steam_iron';
        const subcategoryLower = item.subcategory ? item.subcategory.toLowerCase().trim() : '';
        const isSubcategoryDailyWear = subcategoryLower === 'daily wear' || subcategoryLower.includes('daily');
        
        if (isServiceTypeSteamIron && isSubcategoryDailyWear && remainingCoverage > 0) {
          const itemsToCover = Math.min(item.quantity, remainingCoverage);
          const itemPrice = item.price;
          membershipDiscount += itemsToCover * itemPrice;
          membershipItemsCount += itemsToCover;
          remainingCoverage -= itemsToCover;
        }
      });
    }

    const finalTotal = Math.max(0, regularSubtotal - membershipDiscount);

    return {
      subtotal: regularSubtotal,
      discount: 0,
      total: finalTotal,
      itemCount,
      membershipDiscount,
      membershipItemsCount
    };
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    
    // Store cart in sessionStorage and navigate to checkout
    sessionStorage.setItem('orderCart', JSON.stringify(cart));
    sessionStorage.setItem('orderMembership', JSON.stringify(membership));
    navigate('/place-order');
  };

  const totals = calculateTotal();

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingSpinner>Loading items...</LoadingSpinner>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <Header>
          <Title>Select Items</Title>
        </Header>

        {membership && membership.clothes_remaining > 0 && (
          <MembershipInfo>
            <MembershipText>
              <h3>Active Membership</h3>
              <p>{membership.clothes_remaining} clothes remaining</p>
            </MembershipText>
            <MembershipText>
              <p>Items within your membership limit are FREE!</p>
            </MembershipText>
          </MembershipInfo>
        )}

        <CategoryTabs>
          {categories.map(cat => (
            <CategoryTab
              key={cat.value}
              $active={selectedCategory === cat.value}
              onClick={() => {
                setSelectedCategory(cat.value);
                setSelectedSubcategory('all');
              }}
            >
              {cat.label}
            </CategoryTab>
          ))}
        </CategoryTabs>

        {availableSubcategories.length > 0 && (
          <CategoryTabs style={{ marginTop: '-20px' }}>
            <CategoryTab
              $active={selectedSubcategory === 'all'}
              onClick={() => setSelectedSubcategory('all')}
            >
              All
            </CategoryTab>
            {availableSubcategories.map(subcat => (
              <CategoryTab
                key={subcat}
                $active={selectedSubcategory === subcat}
                onClick={() => setSelectedSubcategory(subcat)}
              >
                {subcat}
              </CategoryTab>
            ))}
          </CategoryTabs>
        )}

        <ItemsGrid>
          {filteredItems.map((item, index) => {
            const inCart = cart.find(c => c.id === item.id);
            
            return (
              <ItemCard
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ItemHeader>
                  <ItemName>{item.item_name}</ItemName>
                  <ItemCategory>{item.category}</ItemCategory>
                </ItemHeader>
                
                {item.subcategory && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--text-secondary)', 
                    marginBottom: '8px',
                    fontStyle: 'italic'
                  }}>
                    {item.subcategory}
                  </div>
                )}

                <ItemPrice>
                  <Price>
                    ₹{item.price}
                  </Price>
                  
                  {(() => {
                    // Only show "FREE with membership" for Steam Iron Daily Wear items
                    const isServiceTypeSteamIron = item.service_type && item.service_type.toLowerCase() === 'steam_iron';
                    const subcategoryLower = item.subcategory ? item.subcategory.toLowerCase().trim() : '';
                    const isSubcategoryDailyWear = subcategoryLower === 'daily wear' || subcategoryLower.includes('daily');
                    
                    if (membership && membership.clothes_remaining > 0 && isServiceTypeSteamIron && isSubcategoryDailyWear) {
                      return <MembershipPrice>FREE with membership</MembershipPrice>;
                    }
                    return null;
                  })()}
                </ItemPrice>

                <ItemActions>
                  {inCart ? (
                    <QuantityControl>
                      <QuantityButton onClick={() => updateQuantity(item.id, -1)}>
                        −
                      </QuantityButton>
                      <Quantity>{inCart.quantity}</Quantity>
                      <QuantityButton onClick={() => updateQuantity(item.id, 1)}>
                        +
                      </QuantityButton>
                    </QuantityControl>
                  ) : (
                    <AddButton onClick={() => addToCart(item)}>
                      <FiPlus /> Add
                    </AddButton>
                  )}
                </ItemActions>
              </ItemCard>
            );
          })}
        </ItemsGrid>

        <AnimatePresence>
          {showCart && (
            <>
              <CartOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowCart(false)}
              />
              <CartSidebar
                initial={{ x: 400 }}
                animate={{ x: 0 }}
                exit={{ x: 400 }}
                transition={{ type: 'spring', damping: 25 }}
              >
                <CartHeader>
                  <CartTitle>Your Cart</CartTitle>
                  <CloseButton onClick={() => setShowCart(false)}>×</CloseButton>
                </CartHeader>

                {cart.length === 0 ? (
                  <EmptyCart>
                    <FiShoppingCart size={48} />
                    <p>Your cart is empty</p>
                  </EmptyCart>
                ) : (
                  <>
                    <CartItems>
                      {cart.map(item => (
                        <CartItem key={item.id}>
                          <CartItemInfo>
                            <h4>{item.item_name}</h4>
                            <p style={{ color: '#999', fontSize: '12px', margin: '4px 0' }}>
                              ₹{item.discounted_price || item.base_price}{item.price_varies ? '+' : ''} each
                            </p>
                            <QuantityControl>
                              <QuantityButton onClick={() => updateQuantity(item.id, -1)}>
                                −
                              </QuantityButton>
                              <Quantity>{item.quantity}</Quantity>
                              <QuantityButton onClick={() => updateQuantity(item.id, 1)}>
                                +
                              </QuantityButton>
                            </QuantityControl>
                          </CartItemInfo>
                          <CartItemPrice>
                            ₹{((item.discounted_price || item.base_price) * item.quantity).toFixed(2)}{item.price_varies ? '+' : ''}
                          </CartItemPrice>
                          <RemoveButton onClick={() => removeFromCart(item.id)}>
                            ×
                          </RemoveButton>
                        </CartItem>
                      ))}
                    </CartItems>

                    <CartSummary>
                      {membership && membership.clothes_remaining > 0 && totals.membershipItemsCount > 0 && (
                        <div style={{ background: '#e8f5e9', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #4caf50' }}>
                          <div style={{ color: '#2e7d32', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>✓ Active Membership</div>
                          <div style={{ color: '#388e3c', fontSize: '12px' }}>{membership.clothes_remaining} clothes remaining • Daily wear items FREE</div>
                        </div>
                      )}
                      
                      <SummaryRow>
                        <span>Total Items:</span>
                        <span>{totals.itemCount}</span>
                      </SummaryRow>

                      <SummaryRow>
                        <span>Subtotal:</span>
                        <span>₹{totals.subtotal.toFixed(2)}</span>
                      </SummaryRow>

                      {totals.membershipDiscount > 0 && (
                        <SummaryRow style={{ color: '#4caf50', fontWeight: 600 }}>
                          <span>Membership Discount:</span>
                          <span>-₹{totals.membershipDiscount.toFixed(2)}</span>
                        </SummaryRow>
                      )}

                      <SummaryRow $total>
                        <span>Total:</span>
                        <span>₹{totals.total.toFixed(2)}</span>
                      </SummaryRow>

                      <CheckoutButton onClick={handleCheckout}>
                        <FiCheck /> Proceed to Checkout
                      </CheckoutButton>
                    </CartSummary>
                  </>
                )}
              </CartSidebar>
            </>
          )}
        </AnimatePresence>
      </Content>
    </Container>
  );
};

export default SelectItems;
